// ============================================================================
// Item detail — the per-item STUDY SPACE (spec §6.3, §6.4, §10.1, §10.2.2).
// For a Hanja: readings (hun/eum) with English + romanization, radical/strokes,
// mnemonic/etymology, the words built from it (word-network) with their example
// sentences, the component family, and confusable characters.
// For a word: hanja form, English + romanization, the component-hanja breakdown
// with the reading used, and example sentences.
// ============================================================================

import { el, mount, toast } from '../dom.js';
import * as api from '@core/data/api.js';
import { romanize } from '@core/hangul/romanize.js';
import { ITEM_TYPE } from '@core/models/constants.js';
import { speakerButton } from '../audio.js';
import { romanizationOn } from '../prefs.js';

const primaryReading = (c) => {
  const rs = c?.character_readings || [];
  const p = rs.find((r) => r.is_primary) || rs[0];
  return p ? `${p.hun ?? ''} ${p.eum ?? ''}`.trim() : '';
};
const primaryEum = (c) => {
  const rs = c?.character_readings || [];
  const p = rs.find((r) => r.is_primary) || rs[0];
  return p?.eum ?? '';
};

function chip(label, value) {
  return el('div.tile', {}, [
    el('div.tile__value', { style: 'font-size:1.2rem', text: value ?? '—' }),
    el('div.tile__label', { text: label }),
  ]);
}

function section(title, body) {
  return el('div.card.stack', {}, [el('h2', { text: title }), body]);
}

// Favorite toggle + add-to-custom-deck control (spec §10.5.3).
function saveControls(type, id, isFav, decks) {
  const favBtn = el('button.btn', { text: isFav ? '★ Favorited' : '☆ Favorite' });
  favBtn.classList.toggle('btn--primary', isFav);
  favBtn.onclick = async () => {
    favBtn.disabled = true;
    try {
      const now = await api.toggleFavorite(type, id);
      favBtn.textContent = now ? '★ Favorited' : '☆ Favorite';
      favBtn.classList.toggle('btn--primary', now);
      toast(now ? 'Added to Favorites' : 'Removed from Favorites', now ? 'success' : 'info');
    } catch (e) { toast(e.message || 'Could not update favorite', 'error'); }
    finally { favBtn.disabled = false; }
  };

  const custom = (decks || []).filter((d) => d.kind !== 'favorite');
  const sel = el('select.input', { style: 'max-width:14rem' }, [
    el('option', { value: '', text: 'Add to deck…' }),
    ...custom.map((d) => el('option', { value: d.id, text: d.name })),
    el('option', { value: '__new__', text: '＋ New deck…' }),
  ]);
  sel.onchange = async () => {
    const v = sel.value; sel.value = '';
    if (!v) return;
    try {
      let deckId = v, name;
      if (v === '__new__') {
        name = (prompt('New deck name:') || '').trim();
        if (!name) return;
        const d = await api.deckCreate(name);
        deckId = d.id;
      } else {
        name = custom.find((d) => d.id === v)?.name || 'deck';
      }
      await api.deckSetItem(deckId, type, id, true);
      toast(`Added to “${name}”`, 'success');
    } catch (e) { toast(e.message || 'Could not add to deck', 'error'); }
  };

  return el('div.row', { style: 'gap:var(--space-2);flex-wrap:wrap;align-items:center' }, [favBtn, sel]);
}

// Personal mnemonic + note for an item (spec §10.3.1), saved via upsert_note.
function notesCard(type, id, existing) {
  const mnem = el('textarea.input', { rows: 2, placeholder: 'A memory hook that works for you…' });
  mnem.value = existing?.mnemonic || '';
  const note = el('textarea.input', { rows: 3, placeholder: 'Anything worth remembering…' });
  note.value = existing?.note || '';
  const status = el('span.faint', {});
  const save = el('button.btn.btn--primary', {
    text: 'Save note',
    onclick: async () => {
      save.disabled = true; status.textContent = 'Saving…';
      try {
        await api.upsertNote(type, id, {
          mnemonic: mnem.value.trim() || null,
          note: note.value.trim() || null,
        });
        status.textContent = 'Saved ✓';
        toast('Note saved', 'success');
      } catch (e) {
        status.textContent = '';
        toast(e.message || 'Could not save note', 'error');
      } finally {
        save.disabled = false;
      }
    },
  });
  return el('div.card.stack', {}, [
    el('h2', { text: 'My notes' }),
    el('div.faint', { text: 'Private to you — a personal mnemonic and any note.' }),
    el('label.faint', { text: 'Mnemonic' }), mnem,
    el('label.faint', { text: 'Note' }), note,
    el('div.row', { style: 'align-items:center;gap:var(--space-3)' }, [save, status]),
  ]);
}

export function renderItemDetail({ navigate, type, id }) {
  const container = el('div.container.stack');
  mount(container, el('div.card', { text: 'Loading…' }));

  const back = el('button.btn.btn--ghost', { text: '← Browse', onclick: () => navigate('#/browse') });

  if (type === ITEM_TYPE.CHARACTER) renderCharacter();
  else renderWord();

  async function renderCharacter() {
    try {
      const [c, words, similar, family, note, fav, decks] = await Promise.all([
        api.getCharacter(id),
        api.wordNetwork(id).catch(() => []),
        api.similarCharacters(id).catch(() => []),
        api.componentFamily(id).catch(() => []),
        api.getNote(ITEM_TYPE.CHARACTER, id).catch(() => null),
        api.isFavorite(ITEM_TYPE.CHARACTER, id).catch(() => false),
        api.myDecks().catch(() => []),
      ]);
      if (!c) return notFound();
      const eum = primaryEum(c);

      // Sentences for the network words, mapped by word_id.
      const allSent = await api.listSentences().catch(() => []);
      const sentByWord = new Map();
      for (const s of allSent) if (s.word_id) (sentByWord.get(s.word_id) || sentByWord.set(s.word_id, []).get(s.word_id)).push(s);

      const header = el('div.card.stack.center', {}, [
        el('div.flashcard__char', { text: c.char }),
        el('div.row.center', { style: 'justify-content:center' }, [speakerButton(() => eum, { label: '🔊 Listen' })]),
        el('div.row.center', { style: 'justify-content:center;gap:var(--space-4)' }, [
          el('div.center', {}, [el('div.faint', { text: 'Reading' }),
            eum
              ? el('button.link-btn', { style: 'font-size:1.4rem', title: 'See all Hanja read this way', text: primaryReading(c) || eum, onclick: () => navigate(`#/reading/${encodeURIComponent(eum)}`) })
              : el('div', { style: 'font-size:1.4rem', text: '—' })]),
          romanizationOn()
            ? el('div.center', {}, [el('div.faint', { text: 'Romanization' }), el('div', { style: 'font-size:1.4rem', text: eum ? romanize(eum) : '—' })])
            : false,
          el('div.center', {}, [el('div.faint', { text: 'English' }), el('div', { style: 'font-size:1.4rem', text: c.meaning_en || '—' })]),
        ]),
      ]);

      const facts = el('div.grid', {}, [
        chip('Radical', c.radical_char || c.radical),
        chip('Strokes', c.stroke_count),
        chip('Tier', c.edu_tier),
        chip('Frequency', c.frequency_rank ? `#${c.frequency_rank}` : '—'),
        chip('Words', words.length),
      ]);

      const allReadings = el('div.stack', {},
        (c.character_readings || []).map((r) =>
          el('div.row', {}, [
            r.eum
              ? el('button.link-btn', { style: 'font-weight:700', title: 'See all Hanja read this way', text: `${r.hun ?? ''} ${r.eum}`.trim(), onclick: () => navigate(`#/reading/${encodeURIComponent(r.eum)}`) })
              : el('strong', { text: `${r.hun ?? ''}`.trim() }),
            r.eum && romanizationOn() ? el('span.faint', { text: `· ${romanize(r.eum)}` }) : false,
            r.is_primary ? el('span.badge-state.state-mastered', { text: 'primary' }) : false,
          ])));

      const wordsBody = words.length
        ? el('div.stack', {}, words.map((w) => {
            const sents = sentByWord.get(w.id) || [];
            return el('div.card', { style: 'padding:var(--space-4)' }, [
              el('div.row', {}, [
                el('button.link-btn', { style: 'font-size:1.3rem;font-family:var(--font-ko)', text: `${w.hangul}  ${w.hanja_form || ''}`, onclick: () => navigate(`#/item/word/${w.id}`) }),
                speakerButton(() => w.hangul),
              ]),
              el('div.faint', { text: romanizationOn() ? `${w.romanization || romanize(w.hangul)} · ${w.meaning_en || ''}` : (w.meaning_en || '') }),
              ...sents.map((s) => el('div.row', { style: 'margin-top:6px' }, [
                el('span.muted', { text: `“${s.text_ko}” — ${s.translation_en || ''}` }),
                speakerButton(() => s.text_ko),
              ])),
            ]);
          }))
        : el('p.muted', { text: 'No linked words yet.' });

      const nodes = [
        el('div.row', {}, [back]),
        header,
        saveControls(ITEM_TYPE.CHARACTER, id, fav, decks),
        facts,
        section('All readings', allReadings),
      ];
      if (c.mnemonic) nodes.push(section('Mnemonic', el('p', { text: c.mnemonic })));
      if (c.etymology) nodes.push(section('Etymology', el('p', { text: c.etymology })));
      nodes.push(section(`Words using ${c.char} (${words.length})`, wordsBody));
      if (family.length) {
        nodes.push(section('Component family', el('div.row', {}, family.map((f) =>
          el('button.btn', { text: f.char, onclick: () => navigate(`#/item/character/${f.id}`) })))));
      }
      if (similar.length) {
        nodes.push(section('Easily confused with', el('div.stack', {}, similar.map((s) =>
          el('div.row', {}, [
            el('button.btn', { text: s.similar?.char || '?', onclick: () => s.similar && navigate(`#/item/character/${s.similar.id}`) }),
            el('span.muted', { text: s.reason || '' }),
          ])))));
      }
      nodes.push(notesCard(ITEM_TYPE.CHARACTER, id, note));
      nodes.push(el('div.row', {}, [
        el('button.btn.btn--primary', { text: 'Practice →', onclick: () => navigate('#/study') }),
      ]));
      mount(container, ...nodes);
    } catch (e) {
      mount(container, errorCard(e));
    }
  }

  async function renderWord() {
    try {
      const [w, sents, note, fav, decks] = await Promise.all([
        api.getWord(id),
        api.sentencesForWord(id).catch(() => []),
        api.getNote(ITEM_TYPE.WORD, id).catch(() => null),
        api.isFavorite(ITEM_TYPE.WORD, id).catch(() => false),
        api.myDecks().catch(() => []),
      ]);
      if (!w) return notFound();

      const header = el('div.card.stack.center', {}, [
        el('div.flashcard__word', { text: w.hangul }),
        el('div', { style: 'font-family:var(--font-hanja);font-size:2rem', text: w.hanja_form || '' }),
        el('div.row.center', { style: 'justify-content:center' }, [speakerButton(() => w.hangul, { label: '🔊 Listen' })]),
        el('div.row.center', { style: 'justify-content:center;gap:var(--space-4)' }, [
          romanizationOn()
            ? el('div.center', {}, [el('div.faint', { text: 'Romanization' }), el('div', { style: 'font-size:1.3rem', text: w.romanization || romanize(w.hangul) })])
            : false,
          el('div.center', {}, [el('div.faint', { text: 'English' }), el('div', { style: 'font-size:1.3rem', text: w.meaning_en || '—' })]),
          el('div.center', {}, [el('div.faint', { text: 'TOPIK' }), el('div', { style: 'font-size:1.3rem', text: w.topik_level ? `Level ${w.topik_level}` : '—' })]),
        ]),
      ]);

      const parts = (w.word_characters || [])
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map((wc) => {
          const c = wc.characters;
          return el('button.btn', { style: 'flex-direction:column;height:auto;padding:var(--space-3)', onclick: () => c && navigate(`#/item/character/${c.id}`) }, [
            el('div', { style: 'font-family:var(--font-hanja);font-size:1.8rem', text: c?.char || '?' }),
            el('div.faint', { text: wc.reading_used || (c ? primaryEum(c) : '') }),
            el('div.faint', { text: c?.meaning_en || '' }),
          ]);
        });

      const nodes = [
        el('div.row', {}, [back]),
        header,
        saveControls(ITEM_TYPE.WORD, id, fav, decks),
        section('Built from', parts.length ? el('div.row', {}, parts) : el('p.muted', { text: 'No component breakdown yet.' })),
        section(`Example sentences (${sents.length})`, sents.length
          ? el('div.stack', {}, sents.map((s) => el('div.card', { style: 'padding:var(--space-4)' }, [
              el('div.row', {}, [
                el('div', { style: 'font-family:var(--font-ko);font-size:1.2rem', text: s.text_ko }),
                speakerButton(() => s.text_ko),
              ]),
              s.romanization && romanizationOn() ? el('div.faint', { text: s.romanization }) : false,
              s.translation_en ? el('div.muted', { text: s.translation_en }) : false,
            ])))
          : el('p.muted', { text: 'No example sentences yet (Claude-generated, approval-gated — spec §12.2).' })),
      ];
      if (w.mnemonic) nodes.push(section('Mnemonic', el('p', { text: w.mnemonic })));
      nodes.push(notesCard(ITEM_TYPE.WORD, id, note));
      nodes.push(el('div.row', {}, [el('button.btn.btn--primary', { text: 'Practice →', onclick: () => navigate('#/study') })]));
      mount(container, ...nodes);
    } catch (e) {
      mount(container, errorCard(e));
    }
  }

  function notFound() {
    mount(container, el('div.card.stack', {}, [back, el('div.banner.banner--warn', { text: 'Item not found or not yet approved.' })]));
  }
  function errorCard(e) {
    return el('div.card.stack', {}, [back, el('div.banner.banner--warn', { text: e.message || 'Could not load item' })]);
  }

  return container;
}
